import React, { useState } from 'react';
import Panel from '../../components/ui/Panel';
import Button from '../../components/ui/Button';
import ComponentShowcase from '../../components/ui/ComponentShowcase';
import Tabs from '../../components/ui/Tabs';

const interfaceTabs = [
  { id: 'stats', label: 'Stats' },
  { id: 'inventory', label: 'Inventory' },
  { id: 'equipment', label: 'Equipment' },
  { id: 'prayer', label: 'Prayer' },
  { id: 'magic', label: 'Magic' },
];

const TabDemo: React.FC = () => {
  const [tab, setTab] = useState('stats');
  return (
    <div>
      <Tabs tabs={interfaceTabs} value={tab} onChange={setTab} label="Interface" />
      <p className="text-osrs-yellow mt-4">Showing {tab}.</p>
    </div>
  );
};

const menuItems = ['Account', 'Inventory', 'Skills', 'Quests', 'Settings'] as const;
type MenuItem = (typeof menuItems)[number];

const MenuBarDemo: React.FC = () => {
  const [active, setActive] = useState<MenuItem>('Account');
  return (
    <Panel>
      <div className="flex flex-wrap gap-1">
        {menuItems.map((item) => (
          <Button
            key={item}
            size="small"
            variant={item === active ? 'primary' : 'secondary'}
            aria-pressed={item === active}
            onClick={() => setActive(item)}
          >
            {item}
          </Button>
        ))}
      </div>
    </Panel>
  );
};

const sidebarItems = ['Equipment', 'Skills', 'Quests', 'Minigames', 'Grand Exchange'] as const;
type SidebarItem = (typeof sidebarItems)[number];

const SidebarDemo: React.FC = () => {
  const [active, setActive] = useState<SidebarItem>('Equipment');
  return (
    <Panel title="Categories" className="w-60">
      <div className="flex flex-col gap-1">
        {sidebarItems.map((item) => (
          <Button
            key={item}
            size="small"
            fullWidth
            variant={item === active ? 'primary' : 'secondary'}
            aria-pressed={item === active}
            onClick={() => setActive(item)}
          >
            {item}
          </Button>
        ))}
      </div>
    </Panel>
  );
};

const dropdownItems = ['View details', 'Edit item', 'Delete item', 'Share item'] as const;

const DropdownDemo: React.FC = () => {
  const [open, setOpen] = useState(true);
  const [picked, setPicked] = useState<(typeof dropdownItems)[number]>('View details');
  return (
    <div>
      <Button aria-expanded={open} onClick={() => setOpen((current) => !current)}>
        {picked}
      </Button>
      {open ? (
        <Panel className="mt-1 w-48">
          <div className="flex flex-col gap-1">
            {dropdownItems.map((item) => (
              <Button
                key={item}
                size="small"
                fullWidth
                variant={item === 'Delete item' ? 'danger' : item === picked ? 'primary' : 'secondary'}
                onClick={() => {
                  setPicked(item);
                  setOpen(false);
                }}
              >
                {item}
              </Button>
            ))}
          </div>
        </Panel>
      ) : null}
    </div>
  );
};

const NavigationPage: React.FC = () => {
  const menuBarCode = `<Panel>
  <Button size="small" variant="primary" aria-pressed>Account</Button>
  <Button size="small" variant="secondary">Inventory</Button>
  <Button size="small" variant="secondary">Skills</Button>
</Panel>`;

  const sidebarCode = `<Panel title="Categories" className="w-60">
  <Button size="small" fullWidth variant="primary" aria-pressed>Equipment</Button>
  <Button size="small" fullWidth variant="secondary">Skills</Button>
  <Button size="small" fullWidth variant="secondary">Quests</Button>
</Panel>`;

  const tabsCode = `<Tabs
  label="Interface"
  value={tab}
  onChange={setTab}
  tabs={[
    { id: 'stats', label: 'Stats' },
    { id: 'inventory', label: 'Inventory' },
    { id: 'equipment', label: 'Equipment' },
    { id: 'prayer', label: 'Prayer' },
    { id: 'magic', label: 'Magic' },
  ]}
/>`;

  const breadcrumbsCode = `<div className="flex items-center text-osrs-yellow mb-4">
  <a href="/home" className="hover:text-osrs-gold">Home</a>
  <span className="mx-2 text-osrs-chat">›</span>
  <a href="/equipment" className="hover:text-osrs-gold">Equipment</a>
  <span className="mx-2 text-osrs-chat">›</span>
  <span className="text-osrs-gold">Weapons</span>
</div>`;

  const dropdownCode = `<Button aria-expanded={open} onClick={() => setOpen(!open)}>
  {picked}
</Button>
{open ? (
  <Panel>
    <Button size="small" fullWidth>View details</Button>
    <Button size="small" fullWidth variant="danger">Delete item</Button>
  </Panel>
) : null}`;

  return (
    <div>
      <h1 id="overview" className="text-3xl text-osrs-gold font-bold uppercase mb-4">Navigation</h1>
      <p className="text-osrs-yellow mb-8">
        Navigation patterns in the OSRS Design System provide consistent ways for users to move through
        the interface, inspired by the distinctive navigation elements of Old School RuneScape.
      </p>

      <section id="menu-bar" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Menu Bar</h2>
        <p className="text-osrs-yellow mb-4">
          The menu bar is a horizontal navigation element that displays primary navigation options.
          It's commonly used at the top of the interface to provide access to main sections.
        </p>
        
        <ComponentShowcase
          title="Menu Bar Example"
          description="A basic menu bar with OSRS styling."
          component={<MenuBarDemo />}
          code={menuBarCode}
          className="mb-10"
        />
      </section>
      
      <section id="sidebar" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Sidebar</h2>
        <p className="text-osrs-yellow mb-4">
          The sidebar provides vertical navigation, typically used for secondary navigation or 
          displaying hierarchical categories. It can be fixed or collapsible depending on the interface needs.
        </p>
        
        <ComponentShowcase
          title="Sidebar Example"
          description="A vertical sidebar with category navigation."
          component={<SidebarDemo />}
          code={sidebarCode}
          className="mb-10"
        />
      </section>
      
      <section id="tabs" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Tabs</h2>
        <p className="text-osrs-yellow mb-4">
          Tabs allow users to switch between related content sections without navigating to a new page.
          They're commonly used in inventory interfaces, skill panels, and settings screens.
        </p>
        
        <ComponentShowcase
          title="Tabs Example"
          description="A tab navigation with OSRS styling."
          component={<TabDemo />}
          code={tabsCode}
          className="mb-10"
        />
      </section>
      
      <section id="breadcrumbs" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Breadcrumbs</h2>
        <p className="text-osrs-yellow mb-4">
          Breadcrumbs show the user's current location in a hierarchical structure and provide 
          a way to navigate back to previous levels.
        </p>
        
        <ComponentShowcase
          title="Breadcrumbs Example"
          description="A breadcrumb navigation trail."
          component={
            <div className="flex items-center text-osrs-yellow mb-4">
              <a href="/home" className="hover:text-osrs-gold">Home</a>
              <span className="mx-2 text-osrs-chat">›</span>
              <a href="/equipment" className="hover:text-osrs-gold">Equipment</a>
              <span className="mx-2 text-osrs-chat">›</span>
              <span className="text-osrs-gold">Weapons</span>
            </div>
          }
          code={breadcrumbsCode}
          className="mb-10"
        />
      </section>
      
      <section id="dropdown" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Dropdown Navigation</h2>
        <p className="text-osrs-yellow mb-4">
          Dropdown navigation conserves space by hiding options until they're needed, making them
          ideal for grouping related actions or navigation items.
        </p>
        
        <ComponentShowcase
          title="Dropdown Example"
          description="A dropdown menu for related actions or navigation."
          component={<DropdownDemo />}
          code={dropdownCode}
          className="mb-10"
        />
      </section>
      
      <section id="usage-guidelines" className="mb-12">
        <h2 className="text-2xl text-osrs-gold font-bold uppercase mb-6">Usage Guidelines</h2>
        
        <Panel className="mb-6">
          <h3 id="choosing-navigation" className="text-xl text-osrs-gold font-bold mb-3">Choosing Navigation Types</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Use menu bars for primary navigation that needs to be always visible</li>
            <li>Use sidebars for hierarchical navigation with multiple levels</li>
            <li>Use tabs for switching between related content views</li>
            <li>Use breadcrumbs for deep hierarchical structures where users need context</li>
            <li>Consider combining navigation types for complex interfaces (e.g., menu bar + sidebar)</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="mobile-considerations" className="text-xl text-osrs-gold font-bold mb-3">Mobile Considerations</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Convert horizontal menu bars to dropdown or hamburger menus on small screens</li>
            <li>Make sidebars collapsible on mobile devices</li>
            <li>Use scrollable tabs when there are many tab options</li>
            <li>Ensure touch targets are at least 44×44 pixels for tappable elements</li>
            <li>Consider simplified navigation patterns for mobile users</li>
          </ul>
        </Panel>
        
        <Panel className="mb-6">
          <h3 id="accessibility" className="text-xl text-osrs-gold font-bold mb-3">Accessibility</h3>
          <ul className="list-disc list-inside text-osrs-yellow space-y-2 ml-4">
            <li>Ensure navigation can be operated with keyboard controls</li>
            <li>Add appropriate ARIA roles for navigation elements</li>
            <li>Use clear, descriptive text for navigation items</li>
            <li>Maintain sufficient color contrast for all navigation states</li>
            <li>Include skip-navigation links for screen reader users</li>
          </ul>
        </Panel>
      </section>
    </div>
  );
};

export default NavigationPage; 